import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { IS_DEMO } from '../../context/AppContext';
import { toast } from '../shared/Toast';
import { storage, auth } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { suggestCategoryAI } from '../../utils/gemini';
import { calcCardDueDate, fmtDateBR } from '../../utils/cards';

const empty = {
  type: 'expense', description: '', value: '',
  date: new Date().toISOString().slice(0, 10),
  categoryId: 'alim', subcategory: '', accountId: '', cardId: '', status: 'pending',
  recurrent: false, frequency: 'monthly', recurrenceCount: 12, installment: false, isSubscription: false, notes: '', tags: [],
};

export default function TransactionModal({ tx, onClose, defaultType, defaultAccountId, defaultCardId }) {
  const { state, dispatch } = useApp();
  const [form, setForm] = useState(
    // Em compra de cartão o campo Data é a data da COMPRA — o vencimento da
    // fatura é calculado no save. Lançamentos antigos (antes do purchaseDate)
    // guardavam só o vencimento: nesses, o campo continua sendo o vencimento
    // pra edição não empurrar a fatura um mês adiante.
    tx ? { ...tx, value: String(tx.value), tags: tx.tags || [], date: tx.purchaseDate || tx.date }
       : { ...empty, type: defaultType || 'expense', accountId: defaultAccountId || state.accounts[0]?.id || '', cardId: defaultCardId || '' }
  );
  const dateIsPurchase = !tx || !!tx.purchaseDate;
  const [tagInput, setTagInput] = useState('');

  // Seções opcionais recolhidas por padrão — o modal fica curto no dia a dia.
  // Ao editar, abre automaticamente as que já têm conteúdo.
  const [openSections, setOpenSections] = useState(() => {
    const s = new Set();
    if (tx?.tags?.length) s.add('tags');
    if (tx?.notes) s.add('notes');
    if (tx?.receiptUrl) s.add('receipt');
    return s;
  });
  const toggleSection = (k) => setOpenSections(prev => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  // ── Categoria automática ─────────────────────────────────────────────
  // 1ª camada: o próprio histórico (mesma descrição → mesma categoria), grátis
  // e instantâneo. 2ª camada (fallback, no blur): Gemini. Só pra lançamento
  // NOVO e enquanto o usuário não mexeu na categoria manualmente.
  const [catSuggested, setCatSuggested] = useState(null); // 'histórico' | 'assistente' | null
  const catTouchedRef = useRef(false);
  const norm = (s) => (s || '').trim().toLowerCase();

  const suggestFromHistory = (desc) => {
    const d = norm(desc);
    if (d.length < 3) return null;
    const counts = {};
    state.transactions.forEach(t => {
      if (t.type !== form.type || !t.categoryId) return;
      const td = norm(t.description);
      if (td === d || (d.length >= 4 && (td.startsWith(d) || d.startsWith(td)))) {
        const key = `${t.categoryId}||${t.subcategory || ''}`;
        counts[key] = (counts[key] || 0) + (td === d ? 3 : 1); // match exato pesa mais
      }
    });
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    if (!best) return null;
    const [categoryId, subcategory] = best[0].split('||');
    // só sugere se a categoria vale pro tipo atual (receita/despesa)
    if (!state.categories.some(c => c.id === categoryId && c.type === form.type)) return null;
    return { categoryId, subcategory };
  };

  const applySuggestion = (sug, source) => {
    // O seletor só exibe subcategorias quando a categoria tem — sem sub a
    // sugestão ficaria invisível; usa a 1ª da categoria como aproximação
    const cat = state.categories.find(c => c.id === sug.categoryId);
    const sub = sug.subcategory || ((cat?.subcategories || [])[0] || '');
    setForm(f => ({ ...f, categoryId: sug.categoryId, subcategory: sub }));
    setCatSuggested(source);
  };

  // Histórico: debounce enquanto digita
  useEffect(() => {
    if (tx || catTouchedRef.current) return;
    const timer = setTimeout(() => {
      const sug = suggestFromHistory(form.description);
      if (sug && (sug.categoryId !== form.categoryId || (sug.subcategory || '') !== (form.subcategory || ''))) {
        applySuggestion(sug, 'histórico');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.description, form.type]); // eslint-disable-line

  // IA: só no blur da descrição, e só se o histórico não achou nada
  const descRef = useRef('');
  useEffect(() => { descRef.current = form.description; }, [form.description]);
  const tryAISuggestion = async () => {
    if (tx || catTouchedRef.current || IS_DEMO) return;
    const desc = form.description;
    if (norm(desc).length < 4 || suggestFromHistory(desc)) return;
    const cats = state.categories.filter(c => c.type === form.type);
    const sug = await suggestCategoryAI(desc, cats);
    // aplica só se a descrição não mudou e a categoria não foi mexida enquanto a IA pensava
    if (sug && !catTouchedRef.current && norm(desc) === norm(descRef.current)) {
      applySuggestion(sug, 'assistente');
    }
  };

  // Tags já usadas em outras transações — para sugestão rápida
  const allTags = useMemo(() => {
    const s = new Set();
    state.transactions.forEach(t => (t.tags || []).forEach(tag => s.add(tag)));
    return [...s].sort();
  }, [state.transactions]);

  const addTag = (raw) => {
    const v = raw.trim().toLowerCase().replace(/,/g, '');
    if (v && !(form.tags || []).includes(v)) {
      setForm(f => ({ ...f, tags: [...(f.tags || []), v] }));
    }
    setTagInput('');
  };
  const removeTag = (tag) => setForm(f => ({ ...f, tags: (f.tags || []).filter(t => t !== tag) }));
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(tx?.receiptUrl || null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const receiptRef = useRef();

  useEffect(() => {
    if (!form.accountId && state.accounts.length) setForm(f => ({ ...f, accountId: state.accounts[0].id }));
  }, [state.accounts, form.accountId]);

  // Compra no cartão nasce pendente — só vira "paga" quando a fatura é paga.
  // A data digitada continua sendo a da compra; o vencimento da fatura é
  // calculado no save (ver calcCardDueDate).
  useEffect(() => {
    if (!tx && form.cardId) setForm(f => ({ ...f, status: 'pending' }));
  }, [form.cardId]); // eslint-disable-line

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const cats = state.categories.filter(c => c.type === form.type);

  useEffect(() => {
    if (cats.length && !cats.find(c => c.id === form.categoryId)) {
      setForm(f => ({ ...f, categoryId: cats[0].id, subcategory: '' }));
    }
  }, [form.type]); // eslint-disable-line

  const catSelectValue = form.subcategory ? `${form.categoryId}||${form.subcategory}` : form.categoryId;

  const handleCatChange = (e) => {
    // usuário escolheu manualmente → para de sugerir nesta edição
    catTouchedRef.current = true;
    setCatSuggested(null);
    const val = e.target.value;
    if (val.includes('||')) {
      const [catId, sub] = val.split('||');
      setForm(f => ({ ...f, categoryId: catId, subcategory: sub }));
    } else {
      setForm(f => ({ ...f, categoryId: val, subcategory: '' }));
    }
  };

  const generateSeries = (base) => {
    const count = Math.max(1, Math.min(base.recurrenceCount || 12, 120));
    const groupId = Date.now().toString();
    const baseDate = new Date(base.date + 'T00:00:00');
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    // Parcelamento: o valor digitado é o TOTAL da compra/dívida, não o valor de cada
    // parcela — divide em centavos (o resto vai pras primeiras parcelas) pra soma bater exata.
    let installmentValues = null;
    if (base.installment && count > 1) {
      const totalCents = Math.round(base.value * 100);
      const baseCents = Math.floor(totalCents / count);
      const remainder = totalCents - baseCents * count;
      installmentValues = Array.from({ length: count }, (_, i) => (baseCents + (i < remainder ? 1 : 0)) / 100);
    }

    return Array.from({ length: count }, (_, i) => {
      let d;
      if (base.frequency === 'weekly') {
        d = new Date(baseDate);
        d.setDate(d.getDate() + i * 7);
      } else if (base.frequency === 'monthly') {
        // preserva o dia do mês com clamp: dia 31 vira 28/29 em fevereiro, sem escorregar de mês
        d = new Date(baseDate.getFullYear(), baseDate.getMonth() + i, 1);
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        d.setDate(Math.min(baseDate.getDate(), lastDay));
      } else {
        d = new Date(baseDate);
        d.setFullYear(d.getFullYear() + i);
      }
      return {
        ...base,
        date: fmt(d),
        status: i === 0 ? base.status : 'pending',
        recurrentGroupId: groupId,
        ...(installmentValues ? { value: installmentValues[i], description: `${base.description} (${i + 1}/${count})` } : {}),
      };
    });
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Arquivo muito grande. Máximo 10MB.'); return; }
    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    if (!form.description.trim()) { toast.error('Descrição obrigatória.'); return; }
    if (!form.value || parseFloat(form.value) <= 0) { toast.error('Valor deve ser maior que zero.'); return; }
    if (!form.date) { toast.error('Data obrigatória.'); return; }
    let payload = { ...form, value: parseFloat(form.value) };
    // Moeda da transação segue a conta escolhida (cartão é sempre BRL).
    // Sempre um valor definido — Firestore rejeita campos undefined.
    payload.currency = (!payload.cardId && state.accounts.find(a => a.id === payload.accountId)?.currency === 'USD') ? 'USD' : 'BRL';

    // Cartão: guarda a data da compra e joga o lançamento pro vencimento da
    // fatura em que ela cai (é assim que extrato/calendário/previsões agrupam).
    // Sem cartão, não sobra purchaseDate de uma edição anterior.
    if (payload.cardId) {
      const cardSel = state.cards.find(c => c.id === payload.cardId);
      if (cardSel && dateIsPurchase) {
        payload.purchaseDate = payload.date;
        payload.date = calcCardDueDate(cardSel, payload.date);
      }
    } else if (payload.purchaseDate) {
      delete payload.purchaseDate;
    }

    // Upload receipt if a new file was selected. Com timeout defensivo: se o
    // Storage estiver indisponível/mal configurado, NÃO trava a tela — salva a
    // transação sem o comprovante e avisa (antes ficava preso em "Enviando...").
    if (receiptFile && !IS_DEMO) {
      setUploadingReceipt(true);
      try {
        const uid = auth.currentUser?.uid;
        if (!uid) throw new Error('sem-login');
        const withTimeout = (p, ms) => Promise.race([
          p,
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
        ]);
        const ext = receiptFile.name.split('.').pop();
        const path = `receipts/${uid}/${Date.now()}.${ext}`;
        const storageRef = ref(storage, path);
        await withTimeout(uploadBytes(storageRef, receiptFile), 30000);
        payload.receiptUrl = await withTimeout(getDownloadURL(storageRef), 15000);
      } catch (e) {
        toast.error('Não consegui enviar o comprovante agora — a transação foi salva sem ele. Tente anexar de novo depois.');
      } finally {
        setUploadingReceipt(false);
      }
    }

    // Aviso de orçamento na hora do lançamento — mesma régua da tela Orçamento
    // Poderoso (despesas pagas do mês corrente). Só avisa ao CRUZAR o limite,
    // pra não repetir o alerta em todo lançamento seguinte.
    const checkBudget = (txp) => {
      if (!txp || txp.type !== 'expense' || txp.status !== 'paid') return;
      const month = new Date().toISOString().slice(0, 7);
      if (!txp.date?.startsWith(month)) return;
      const budget = (state.budgets || []).find(b => b.categoryId === txp.categoryId);
      if (!budget || !(budget.planned > 0)) return;
      const before = state.transactions
        .filter(t => t.date?.startsWith(month) && t.type === 'expense' && t.status === 'paid' && t.categoryId === txp.categoryId)
        .reduce((s, t) => s + (t.value || 0), 0);
      const after = before + txp.value;
      const catName = state.categories.find(c => c.id === txp.categoryId)?.name || 'esta categoria';
      const fmt = (v) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const alertPct = budget.alertPct || 80;
      if (after > budget.planned && before <= budget.planned) {
        toast.error(`🚨 Orçamento de ${catName} estourou: ${fmt(after)} de ${fmt(budget.planned)} no mês.`);
        dispatch({ type: 'ADD_NOTIFICATION', payload: { title: `Orçamento de ${catName} estourado`, message: `${fmt(after)} gastos de ${fmt(budget.planned)} planejados este mês.`, type: 'warning' } });
      } else if ((after / budget.planned) * 100 >= alertPct && (before / budget.planned) * 100 < alertPct) {
        toast.info(`⚠️ Orçamento de ${catName}: ${Math.round(after / budget.planned * 100)}% usado (${fmt(after)} de ${fmt(budget.planned)}).`);
        dispatch({ type: 'ADD_NOTIFICATION', payload: { title: `Orçamento de ${catName} em ${Math.round(after / budget.planned * 100)}%`, message: `${fmt(after)} gastos de ${fmt(budget.planned)} planejados este mês.`, type: 'warning' } });
      }
    };

    // Guarda-costas "na hora": avisa NO ATO quando o lançamento foge do padrão —
    // gasto grande ou compra parcelada nova. Complementa o Check de Rota das 20h
    // (aqui é instantâneo). Só em lançamento NOVO (não na edição de antigos).
    const BIG_EXPENSE = state.bigExpenseAlert || 200; // R$ — ajustável em Configurações
    const checkGuardaRota = (txp, series) => {
      if (!txp || txp.type !== 'expense') return;
      const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      // Compra parcelada nova (>1 parcela, não é assinatura) — tem prioridade
      if (series && series.length > 1 && !txp.isSubscription) {
        toast.info(`➕ Compra parcelada registrada em ${series.length}x. Fica de olho pra não sair da rota! 🚦`);
        dispatch({ type: 'ADD_NOTIFICATION', payload: { title: 'Compra parcelada nova', message: `${txp.description} em ${series.length}x — atenção pra não virar bola de neve.`, type: 'warning' } });
        return;
      }
      // Gasto grande (acima do seu limite de rota)
      if ((txp.value || 0) >= BIG_EXPENSE) {
        toast.info(`🚦 Gasto acima da sua rota (${fmt(BIG_EXPENSE)}): ${txp.description} — ${fmt(txp.value)}.`);
        dispatch({ type: 'ADD_NOTIFICATION', payload: { title: 'Gasto alto registrado', message: `${txp.description} — ${fmt(txp.value)} (seu alerta de rota é ${fmt(BIG_EXPENSE)}).`, type: 'warning' } });
      }
    };

    if (tx) {
      dispatch({ type: 'UPDATE_TRANSACTION', payload });
      // "É uma assinatura" mudou num item de série recorrente → aplica na série
      // inteira, senão só um mês apareceria na aba Assinaturas
      if (tx.recurrentGroupId && !!payload.isSubscription !== !!tx.isSubscription) {
        dispatch({ type: 'SET_GROUP_SUBSCRIPTION', payload: { groupId: tx.recurrentGroupId, value: !!payload.isSubscription } });
      }
    } else if (payload.recurrent) {
      const series = generateSeries(payload);
      dispatch({ type: 'ADD_TRANSACTION_SERIES', payload: series });
      checkBudget(series[0]);
      checkGuardaRota(series[0], series);
    } else {
      dispatch({ type: 'ADD_TRANSACTION', payload });
      checkBudget(payload);
      checkGuardaRota(payload, null);
    }
    dispatch({ type: 'ADD_NOTIFICATION', payload: { title: tx ? 'Transação atualizada' : 'Transação adicionada', message: `${form.description} — R$ ${parseFloat(form.value).toFixed(2)}`, type: 'success' } });
    onClose();
  };

  const selectedCard = form.cardId ? state.cards.find(c => c.id === form.cardId) : null;
  // Conta em dólar (sem cartão): o valor digitado é em US$ e a transação guarda currency
  const selectedAccount = state.accounts.find(a => a.id === form.accountId);
  const isUsdTx = !form.cardId && selectedAccount?.currency === 'USD';
  const moeda = isUsdTx ? 'US$' : 'R$';

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">
          {tx ? 'Editar transação' : 'Nova transação'}
          <button className="close-btn" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div className="type-toggle">
          <button className={`type-btn ${form.type === 'income' ? 'active-income' : ''}`} onClick={() => set('type', 'income')}>
            <i className="fas fa-arrow-up"></i> Receita
          </button>
          <button className={`type-btn ${form.type === 'expense' ? 'active-expense' : ''}`} onClick={() => set('type', 'expense')}>
            <i className="fas fa-arrow-down"></i> Despesa
          </button>
        </div>

        <div className="form-group">
          <label className="form-label">Descrição *</label>
          <input className="form-control" type="text" placeholder="Ex: Supermercado" value={form.description} onChange={e => set('description', e.target.value)} onBlur={tryAISuggestion} autoFocus />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              {form.recurrent && form.installment ? `Valor total (${moeda}) *` : `Valor (${moeda}) *`}
              {isUsdTx && <span style={{ color: 'var(--gold)', fontSize: 10, marginLeft: 6 }}>conta em dólar</span>}
            </label>
            <input className="form-control" type="number" placeholder="0,00" step="0.01" min="0" value={form.value} onChange={e => set('value', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">
              {selectedCard && dateIsPurchase ? 'Data da compra' : 'Data'}
              {selectedCard && !dateIsPurchase && <span style={{ color: 'var(--blue)', fontSize: 10, marginLeft: 6 }}>venc. da fatura</span>}
            </label>
            <input className="form-control" type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            {selectedCard && dateIsPurchase && form.date && (
              <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 4 }}>
                <i className="fas fa-file-invoice-dollar" style={{ marginRight: 4 }}></i>
                Entra na fatura que vence em {fmtDateBR(calcCardDueDate(selectedCard, form.date))}
              </div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Categoria</label>
            <select className="form-control" value={catSelectValue} onChange={handleCatChange}>
              {cats.map(c => {
                const subs = c.subcategories || [];
                if (subs.length === 0) {
                  return <option key={c.id} value={c.id}>{c.name}</option>;
                }
                return (
                  <optgroup key={c.id} label={c.name}>
                    {subs.map(sub => (
                      <option key={sub} value={`${c.id}||${sub}`}>{sub}</option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            {catSuggested && (
              <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 4 }}>
                ✨ Sugerida pelo seu {catSuggested} — troque se não for isso.
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Conta</label>
            <select className="form-control" value={form.accountId} onChange={e => set('accountId', e.target.value)}>
              {state.accounts.filter(a => a.active).map(a => <option key={a.id} value={a.id}>{a.name}{a.currency === 'USD' ? ' (US$)' : ''}</option>)}
            </select>
          </div>
        </div>

        {form.type === 'expense' && state.cards.length > 0 && (
          <div className="form-group">
            <label className="form-label">Cartão de crédito <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(opcional)</span></label>
            <select className="form-control" value={form.cardId} onChange={e => set('cardId', e.target.value)}>
              <option value="">— Débito / Dinheiro</option>
              {state.cards.map(c => (
                <option key={c.id} value={c.id}>{c.name} •• {c.lastDigits} · vence dia {c.dueDay}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="paid">Pago</option>
              <option value="pending">Pendente</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Recorrência</label>
            {tx ? (
              // Editando: a série já foi criada — mudar recorrência aqui não cria
              // nem remove lançamentos, então mostramos só a informação
              <div className="form-control" style={{ display: 'flex', alignItems: 'center', color: 'var(--muted)', fontSize: 13, background: 'var(--surface2)' }}>
                {tx.recurrentGroupId ? <><i className="fas fa-repeat" style={{ marginRight: 6, fontSize: 11 }}></i>Faz parte de uma série</> : 'Lançamento único'}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select className="form-control" value={form.recurrent ? form.frequency : 'none'} onChange={e => { if (e.target.value === 'none') set('recurrent', false); else { set('recurrent', true); set('frequency', e.target.value); } }}>
                    <option value="none">Não repete</option>
                    <option value="weekly">Semanal</option>
                    <option value="monthly">Mensal</option>
                    <option value="yearly">Anual</option>
                  </select>
                  {form.recurrent && (
                    <input
                      className="form-control"
                      type="number" min="1" max="120"
                      value={form.recurrenceCount}
                      onChange={e => set('recurrenceCount', parseInt(e.target.value) || 1)}
                      title="Quantas vezes repetir"
                      style={{ width: 70, flexShrink: 0 }}
                    />
                  )}
                </div>
                {form.recurrent && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Repetir {form.recurrenceCount}x — {form.recurrenceCount} lançamentos serão criados</div>}
                {form.recurrent && form.recurrenceCount > 1 && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.installment} onChange={e => set('installment', e.target.checked)} />
                    Valor digitado é o total — dividir em {form.recurrenceCount} parcelas
                  </label>
                )}
                {form.recurrent && form.installment && form.recurrenceCount > 1 && !!form.value && (
                  <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 4 }}>
                    <i className="fas fa-calculator" style={{ marginRight: 4 }}></i>
                    {form.recurrenceCount}x de R$ {(parseFloat(form.value) / form.recurrenceCount).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} — cada parcela fica "1/{form.recurrenceCount}", "2/{form.recurrenceCount}" etc. na descrição
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Assinatura: ao criar, aparece pra despesa recorrente mensal; ao editar,
            aparece pra qualquer despesa — marcar aplica na série inteira (save) */}
        {form.type === 'expense' && !form.installment && (tx || (form.recurrent && form.frequency === 'monthly')) && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: -6, marginBottom: 14, fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.isSubscription} onChange={e => set('isSubscription', e.target.checked)} />
            É uma assinatura (Netflix, Spotify, academia...) — aparece na aba Assinaturas
          </label>
        )}

        {/* Extras opcionais — recolhidos por padrão pra manter o modal curto */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button type="button" className={`opt-chip ${openSections.has('tags') ? 'open' : ''} ${(form.tags || []).length ? 'filled' : ''}`} onClick={() => toggleSection('tags')}>
            <i className="fas fa-tag"></i> Tags{(form.tags || []).length ? ` (${form.tags.length})` : ''}
          </button>
          <button type="button" className={`opt-chip ${openSections.has('notes') ? 'open' : ''} ${form.notes ? 'filled' : ''}`} onClick={() => toggleSection('notes')}>
            <i className="fas fa-note-sticky"></i> Observações
          </button>
          {!IS_DEMO && (
            <button type="button" className={`opt-chip ${openSections.has('receipt') ? 'open' : ''} ${receiptPreview ? 'filled' : ''}`} onClick={() => toggleSection('receipt')}>
              <i className="fas fa-paperclip"></i> Comprovante
            </button>
          )}
        </div>

        {openSections.has('tags') && (
        <div className="form-group">
          <label className="form-label">Tags <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(opcional — Enter para adicionar)</span></label>
          {(form.tags || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {form.tags.map(tag => (
                <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.35)', color: 'var(--gold)', borderRadius: 99, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                  #{tag}
                  <i className="fas fa-times" style={{ fontSize: 10, cursor: 'pointer', opacity: 0.7 }} onClick={() => removeTag(tag)}></i>
                </span>
              ))}
            </div>
          )}
          <input
            className="form-control"
            placeholder="Ex: viagem, trabalho, urgente..."
            value={tagInput}
            onChange={e => {
              const v = e.target.value;
              if (v.endsWith(',')) addTag(v);
              else setTagInput(v);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); }
              if (e.key === 'Backspace' && !tagInput && (form.tags || []).length > 0) removeTag(form.tags[form.tags.length - 1]);
            }}
            onBlur={() => tagInput.trim() && addTag(tagInput)}
          />
          {tagInput && allTags.filter(t => t.startsWith(tagInput.toLowerCase()) && !(form.tags || []).includes(t)).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {allTags.filter(t => t.startsWith(tagInput.toLowerCase()) && !(form.tags || []).includes(t)).slice(0, 5).map(t => (
                <button key={t} type="button" onClick={() => addTag(t)} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 99, padding: '3px 10px', fontSize: 11, color: 'var(--text2)', cursor: 'pointer' }}>
                  #{t}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {openSections.has('notes') && (
        <div className="form-group">
          <label className="form-label">Observações</label>
          <textarea className="form-control" rows={2} placeholder="Opcional..." value={form.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'none' }} />
        </div>
        )}

        {/* Comprovante */}
        {!IS_DEMO && openSections.has('receipt') && (
          <div className="form-group">
            <label className="form-label">Comprovante <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(opcional)</span></label>
            <input ref={receiptRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleReceiptChange} />
            {receiptPreview ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                {receiptPreview.startsWith('blob:') || receiptPreview.match(/\.(jpg|jpeg|png|gif|webp)/i) ? (
                  <img src={receiptPreview} alt="Comprovante" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                ) : (
                  <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fee2e2', borderRadius: 6 }}>
                    <i className="fas fa-file-pdf" style={{ color: '#e24b4a', fontSize: 20 }}></i>
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{receiptFile?.name || 'Comprovante anexado'}</div>
                  <button onClick={() => { setReceiptFile(null); setReceiptPreview(null); set('receiptUrl', ''); }} style={{ background: 'none', border: 'none', color: '#e24b4a', fontSize: 11, cursor: 'pointer', padding: 0 }}>
                    <i className="fas fa-times" style={{ marginRight: 3 }}></i>Remover
                  </button>
                </div>
                <button onClick={() => window.open(receiptPreview, '_blank')} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16 }}>
                  <i className="fas fa-external-link-alt"></i>
                </button>
              </div>
            ) : (
              <button
                onClick={() => receiptRef.current?.click()}
                style={{ width: '100%', padding: '10px', background: 'var(--surface2)', border: '1px dashed var(--border)', borderRadius: 10, cursor: 'pointer', color: 'var(--muted)', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#378ADD'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <i className="fas fa-paperclip"></i> Anexar comprovante (foto ou PDF)
              </button>
            )}
          </div>
        )}

        <button className="btn btn-primary btn-full" onClick={save} disabled={uploadingReceipt} style={{ padding: 12 }}>
          {uploadingReceipt
            ? <><i className="fas fa-circle-notch fa-spin"></i> Enviando comprovante...</>
            : <><i className="fas fa-check"></i> Salvar transação</>
          }
        </button>
      </div>
    </div>
  );
}
